import { ReactElement } from "react";

import { Box } from "@twilio-paste/core/box";
import { styled } from "@twilio-paste/core/styling-library";

import {
  MessageBlockRole,
  MessageCompletionStatus,
  WiseOwlElementIds,
} from "@twiliointernal/e2-commons-shared-definitions";
import { useWiseOwlContext } from "../../../hooks";
import { types } from "../definitions";
import MessageSkeletonLoader from "./MessageSkeletonLoader";
import RetryRegenerateRenderer from "./RetryRegenerateRenderer";
import {
  AvatarWrapper,
  MessageMarkdown,
} from "@twiliointernal/e2-commons-shared-ui";

const MessageRendererContainer = styled(Box)`
  [data-paste-element="MEDIA_OBJECT"] {
    flex-direction: column;
  }
`;

// Renders skeleton loader while markdown length is below a certain threshold
const StreamedMessageMarkdown = ({
  markdown,
  loadThreshold,
}: {
  markdown: string;
  loadThreshold: number;
}): ReactElement => {
  if (markdown.length < loadThreshold) {
    return <MessageSkeletonLoader />;
  }

  return <MessageMarkdown markdown={markdown} />;
};

const MessageRenderer = ({
  block,
}: types.MessageRendererProps): ReactElement => {
  const { author } = useWiseOwlContext();
  const { role, message, isComplete, completionStatus } = block.values;

  const isAssistant = role === MessageBlockRole.Assistant;
  const isMessageError = completionStatus === MessageCompletionStatus.Error;
  const isAssistantMessageStreaming =
    isAssistant && !isComplete && !isMessageError;
  const avatarWrapperProps = isAssistant
    ? {
        role: MessageBlockRole.Assistant,
      }
    : {
        role: MessageBlockRole.User,
        author,
      };

  return (
    <MessageRendererContainer
      as="div"
      element={WiseOwlElementIds.WISE_OWL_MESSAGE_BLOCK}
    >
      <AvatarWrapper {...avatarWrapperProps}>
        {/* Markdown */}
        {isAssistantMessageStreaming ? (
          <StreamedMessageMarkdown markdown={message} loadThreshold={300} />
        ) : (
          <>
            <MessageMarkdown markdown={message} />
            {/* Retry Message */}
            <RetryRegenerateRenderer block={block} />
          </>
        )}
      </AvatarWrapper>
    </MessageRendererContainer>
  );
};

export default MessageRenderer;
